import React, { useMemo, useState } from 'react';
import {
  DAY_LABELS,
  buildCalendarWeeks,
  formatDateKey,
  formatLongDate,
  formatPriceRange,
  formatSessionRange,
  getDateAvailability,
  getInitialMonth,
  groupSessionsByDate,
} from '@/lib/eventSessions';
import { resolveCheckoutUrl } from '@/lib/trybooking';
import { isTryBookingUrl } from '@/data/trybookingEvents';

const CALENDAR_NAVY = '#004768';
const CALENDAR_TEAL = '#0099a8';
const CALENDAR_ORANGE = '#e67e22';
const SELECT_GOLD = '#ebb413';

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-xs text-foreground/80">
      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}

function SessionCard({ session, eventTitle, ticketUrl, activeCheckoutUrl, onSelectSession }) {
  const isAvailable = session.status === 'available';
  const checkoutUrl = resolveCheckoutUrl(session, ticketUrl);
  const isActive = activeCheckoutUrl === checkoutUrl;

  return (
    <div className="border-t border-border/60 px-4 py-5 space-y-3">
      {session.headline ? (
        <p className="text-sm font-bold text-red-600">{session.headline}</p>
      ) : null}
      {session.tagline ? (
        <p className="text-sm italic text-blue-700">{session.tagline}</p>
      ) : null}
      <p className="text-sm font-bold text-emerald-700">{eventTitle}</p>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <p className="text-sm font-bold text-foreground">{formatSessionRange(session)}</p>
          <p className="text-sm font-semibold text-foreground">{formatPriceRange(session)}</p>
          <p className="text-sm text-muted-foreground">{session.description}</p>
          <div className="flex items-center gap-2 text-sm">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{
                backgroundColor:
                  session.status === 'available'
                    ? CALENDAR_TEAL
                    : session.status === 'unavailable'
                      ? CALENDAR_ORANGE
                      : '#cbd5e1',
              }}
            />
            <span className="capitalize">
              {session.status === 'past' ? 'Past session' : session.status}
            </span>
          </div>
        </div>

        {isAvailable ? (
          <button
            type="button"
            onClick={() => onSelectSession(session, checkoutUrl)}
            className={`inline-flex shrink-0 items-center justify-center px-8 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-opacity hover:opacity-90 ${
              isActive ? 'ring-2 ring-offset-2 ring-primary' : ''
            }`}
            style={{ backgroundColor: SELECT_GOLD }}
          >
            Select
          </button>
        ) : (
          <button
            type="button"
            disabled
            className="inline-flex shrink-0 cursor-not-allowed items-center justify-center px-8 py-2.5 text-xs font-bold uppercase tracking-widest text-white/70"
            style={{ backgroundColor: '#94a3b8' }}
          >
            Select
          </button>
        )}
      </div>
    </div>
  );
}

export default function EventBookingCalendar({ eventTitle, sessions, ticketUrl, onCheckoutOpen }) {
  const sessionsByDate = useMemo(() => groupSessionsByDate(sessions), [sessions]);
  const [visibleMonth, setVisibleMonth] = useState(() => getInitialMonth(sessions));
  const [selectedDate, setSelectedDate] = useState(() => {
    const firstAvailable = sessions.find((session) => session.status === 'available');
    return firstAvailable?.date ?? sessions[0]?.date ?? null;
  });
  const [activeCheckoutUrl, setActiveCheckoutUrl] = useState(null);

  const weeks = useMemo(
    () => buildCalendarWeeks(visibleMonth.getFullYear(), visibleMonth.getMonth()),
    [visibleMonth],
  );

  const monthLabel = visibleMonth.toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
  });

  const selectedSessions = selectedDate ? sessionsByDate.get(selectedDate) ?? [] : [];

  const shiftMonth = (delta) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  };

  const handleSelectSession = (session, checkoutUrl) => {
    if (onCheckoutOpen && isTryBookingUrl(checkoutUrl)) {
      onCheckoutOpen({ url: checkoutUrl, label: formatSessionRange(session) });
      return;
    }
    setActiveCheckoutUrl(checkoutUrl);
  };

  return (
    <div id="booking" className="overflow-hidden rounded-xl border border-border/70 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="px-3 py-1 text-sm font-semibold text-foreground/70 hover:text-foreground"
          aria-label="Previous month"
        >
          ‹
        </button>
        <p className="text-sm font-bold uppercase tracking-wide text-foreground">{monthLabel}</p>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="px-3 py-1 text-sm font-semibold text-foreground/70 hover:text-foreground"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div style={{ backgroundColor: CALENDAR_NAVY }}>
        <div className="grid grid-cols-7">
          {DAY_LABELS.map((label) => (
            <div
              key={label}
              className="py-2 text-center text-[11px] font-bold tracking-wide text-white"
            >
              {label}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-7 border-l border-t border-border/50">
        {weeks.flat().map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="min-h-[52px] border-r border-b border-border/50 bg-muted/20" />;
          }

          const dateKey = formatDateKey(date);
          const availability = getDateAvailability(dateKey, sessionsByDate);
          const isSelected = selectedDate === dateKey;
          const isCurrentMonth = date.getMonth() === visibleMonth.getMonth();

          let textClass = 'text-foreground/35';
          if (availability === 'available') textClass = 'font-bold text-foreground';
          if (availability === 'unavailable') textClass = 'font-semibold text-foreground/70';
          if (availability === 'past') textClass = 'text-foreground/35';

          return (
            <button
              key={dateKey}
              type="button"
              disabled={!availability}
              onClick={() => {
                setSelectedDate(dateKey);
                setActiveCheckoutUrl(null);
              }}
              className={`relative min-h-[52px] border-r border-b border-border/50 text-sm transition-colors ${
                isSelected ? 'bg-muted/50' : 'bg-white hover:bg-muted/20'
              } ${!availability ? 'cursor-default' : 'cursor-pointer'} ${!isCurrentMonth ? 'opacity-40' : ''}`}
            >
              <span className={`inline-flex h-full w-full items-center justify-center ${textClass}`}>
                {date.getDate()}
              </span>
              {availability === 'available' ? (
                <span
                  className="absolute bottom-0 left-1 right-1 h-1 rounded-full"
                  style={{ backgroundColor: isSelected ? CALENDAR_TEAL : '#cbd5e1' }}
                />
              ) : null}
              {availability === 'unavailable' ? (
                <span
                  className="absolute bottom-0 left-1 right-1 h-1 rounded-full"
                  style={{ backgroundColor: CALENDAR_ORANGE }}
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-5 px-4 py-3 border-t border-border/60 bg-muted/10">
        <LegendItem color={CALENDAR_TEAL} label="Available" />
        <LegendItem color={CALENDAR_ORANGE} label="Not available" />
        <LegendItem color="#cbd5e1" label="Past session" />
      </div>

      {selectedDate ? (
        <>
          <div className="px-4 py-4 text-white" style={{ backgroundColor: CALENDAR_NAVY }}>
            <p className="text-sm font-medium">Select a time</p>
            <p className="text-lg font-bold">{formatLongDate(selectedDate)}</p>
          </div>

          {selectedSessions.length ? (
            selectedSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                eventTitle={eventTitle}
                ticketUrl={ticketUrl}
                activeCheckoutUrl={activeCheckoutUrl}
                onSelectSession={handleSelectSession}
              />
            ))
          ) : (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              No sessions scheduled for this date.
            </p>
          )}
        </>
      ) : (
        <p className="px-4 py-6 text-sm text-muted-foreground">
          Select an available date to view session times and pricing.
        </p>
      )}
    </div>
  );
}
